import { Component, ElementRef, EventEmitter, forwardRef, inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { gungGetPropertyOfObject } from '../../utils/gung-utils';
import { BaseInputDirective } from '../base-input/base-input.directive';
import { OptionsList } from '../gung-select-option-input/gung-select-option-input.component';

export interface OptionsListMulti extends OptionsList {
  selected: boolean;
  hide?: boolean;
  _badgeValue?: string;
  _imageValue?: string;
}

@Component({
  selector: 'lib-gung-input-multi-select',
  templateUrl: './gung-input-multi-select.component.html',
  styleUrls: ['./gung-input-multi-select.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GungInputMultiSelectComponent),
      multi: true
    }
  ],
  standalone: false
})
export class GungInputMultiSelectComponent extends BaseInputDirective<string[]> implements OnInit, OnChanges {
  @Input()
  public label: string;

  @Input()
  public showId = true;

  @Input()
  previewEmpty = 'Choose available options';

  @Input()
  public tooltip?: string;
  @Input('optionsList') set setOptionList(value: OptionsListMulti[]) {
    this.optionsList = JSON.parse(JSON.stringify(value));
    this.ngOnInit();
  }
  @Input()
  public previewField?: string | string[];

  @Input()
  public optionBadgeField?: string | string[];

  @Input()
  public optionImageField?: string | string[];

  @Input()
  public dropdownCustomClass: string;

  @Input()
  public optionsList: OptionsListMulti[];
  optionsFilterList: OptionsListMulti[];

  @Output()
  selectedValues = new EventEmitter<string[]>();

  @Output()
  selectedValuesClose = new EventEmitter<string[]>();
  changedBeforeClose = false;

  dropdownIsOpen = false;
  searchString: string;
  currentSelectedValues: string[];

  previewList: string[];

  @Input()
  singleSelection: boolean;

  @Input()
  disabled: boolean;

  @ViewChild('dropDownEle')
  public dropDownEle: NgbDropdown;

  @Input()
  focusInput?: boolean;

  @Input()
  showEmptyIds?: boolean;

  @ViewChild('filterListSearch') filterListSearch: any;

  @Input()
  ngBootstrapContainer: 'body' | null = null;

  @Input()
  suppressSearch = false;

  @Input()
  enableSelectAll = false;

  @Input()
  enableLazyLoad = false;

  @Input()
  lazyLoadLimit = 25;

  @Input()
  exactMatchFirst = false;

  private elementRef = inject(ElementRef);
  activeIndex = -1;
  showAll = false;
  visibleOptions: OptionsListMulti[] = [];
  hasMoreOptions = false;

  private resolveField(field: string | string[] | undefined, option: OptionsListMulti): string {
    if (!field) return '';
    if (Array.isArray(field)) {
      return field
        .map(f => (['', ' ', ',', ', ', ' - ', '-'].includes(f) ? f : gungGetPropertyOfObject(f, option)))
        .join('');
    }
    return gungGetPropertyOfObject(field, option) || '';
  }

  private resolveOptionFields(list: OptionsListMulti[]): OptionsListMulti[] {
    return list.map(o => ({
      ...o,
      _badgeValue: this.resolveField(this.optionBadgeField, o),
      _imageValue: this.resolveField(this.optionImageField, o)
    }));
  }

  private updateVisibleOptions(): void {
    if (!this.enableLazyLoad || this.showAll || this.searchString) {
      this.visibleOptions = this.resolveOptionFields(this.optionsFilterList);
      this.hasMoreOptions = false;
      return;
    }
    const selected = this.optionsFilterList.filter(o => o.selected);
    const unselected = this.optionsFilterList.filter(o => !o.selected);
    this.hasMoreOptions = unselected.length > this.lazyLoadLimit;
    this.visibleOptions = this.resolveOptionFields([...selected, ...unselected.slice(0, this.lazyLoadLimit)]);
  }

  onShowAll(): void {
    this.showAll = true;
    this.updateVisibleOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.optionsFilterList = this.searchString
      ? this.searchOptions(this.searchString, this.optionsList)
      : this.optionsList;

    this.currentSelectedValues = this.optionsList.filter(o => o.selected).map(o => o.id);
    this.updateVisibleOptions();
    this.updatePreview();
  }

  ngOnInit(): void {
    this.optionsFilterList = this.searchString
      ? this.searchOptions(this.searchString, this.optionsList)
      : this.optionsList;

    this.currentSelectedValues = this.optionsList.filter(o => o.selected).map(o => o.id);
    this.updateVisibleOptions();
    this.updatePreview();
  }

  private updatePreview() {
    this.previewList = this.optionsList
      .filter(o => this.currentSelectedValues.includes(o.id))
      .map(o => {
        if (this.previewField) {
          if (Array.isArray(this.previewField)) {
            const opts = this.previewField.map(field => {
              if (['', ' ', ',', ', ', ' - ', '-'].includes(field)) {
                return field;
              }
              return gungGetPropertyOfObject(field, o);
            });

            if (this.showEmptyIds) {
              const newOpts: string[] = [];
              for (let index = 0; index < opts.length; index++) {
                const previousOpt = opts[index - 1];
                const opt = opts[index];
                if (!(['', ' ', ',', ', ', ' - ', '-'].includes(opt) && !previousOpt)) {
                  newOpts.push(opt);
                }
              }
              return newOpts.join('');
            }

            return opts.join('');
          }
          return o[this.previewField];
        }
        return o.id;
      });
  }

  setSearch(search: string): void {
    this.searchString = search;
    this.activeIndex = -1;
    this.optionsFilterList = (!search || search.trim() === '')
      ? this.optionsList
      : this.searchOptions(search, this.optionsList);
    this.updateVisibleOptions();
  }

  private searchOptions(searchTerm: string, items: OptionsListMulti[]): OptionsListMulti[] {
    const filtered: OptionsListMulti[] = this.filterBySearchTerm(searchTerm, items);
    if (!this.exactMatchFirst) {
      return filtered;
    }
    const query = searchTerm.trim().toUpperCase();
    const isExact = (o: OptionsListMulti) => o.id?.toUpperCase() === query || o.name?.toUpperCase() === query;
    return [...filtered.filter(isExact), ...filtered.filter(o => !isExact(o))];
  }

  filterBySearchTerm(searchTerm: string, items: OptionsList[]): any[] {
    return items.filter(item => {
      let hasHitAllTerms = true;
      const queryTerms = searchTerm.split(' ');
      const terms = [item.id, item.name];
      if (this.previewField) {
        let previewValue = undefined;
        if (Array.isArray(this.previewField)) {
          previewValue = this.previewField
            .map(field => {
              if (['', ' ', ',', ', ', ' - ', '-'].includes(field)) {
                return field;
              }
              return gungGetPropertyOfObject(field, item);
            })
            .join('');
        } else {
          previewValue = item[this.previewField];
        }
        terms.push(previewValue);
      }
      queryTerms.forEach(queryTerm => {
        const locatedTerm = terms.find(term => {
          if (term === null || term === undefined) {
            return false;
          }
          return term.toUpperCase().indexOf(queryTerm.toUpperCase()) >= 0;
        });

        hasHitAllTerms = hasHitAllTerms && !!locatedTerm;
      });

      return hasHitAllTerms;
    });
  }

  get selectableFilteredOptions(): OptionsListMulti[] {
    return (this.optionsFilterList || []).filter(o => !o.hide && (!!o.id || this.showEmptyIds));
  }

  get allFilteredSelected(): boolean {
    const selectable = this.selectableFilteredOptions;
    return selectable.length > 0 && selectable.every(o => this.currentSelectedValues.includes(o.id));
  }

  toggleSelectAll(): void {
    const selectAll = !this.allFilteredSelected;
    const ids = new Set(this.selectableFilteredOptions.map(o => o.id));
    if (selectAll) {
      ids.forEach(id => {
        if (!this.currentSelectedValues.includes(id)) {
          this.currentSelectedValues.push(id);
        }
      });
    } else {
      this.currentSelectedValues = this.currentSelectedValues.filter(id => !ids.has(id));
    }
    this.optionsList.forEach(o => (o.selected = this.currentSelectedValues.includes(o.id)));
    this.updateVisibleOptions();
    this.updatePreview();
    this.selectedValues.emit(this.currentSelectedValues);
    this.changedBeforeClose = true;
  }

  setSelectValue(valueId: string, selected: boolean) {
    if (this.singleSelection) {
      this.currentSelectedValues = [valueId];
      this.optionsList.forEach(o => (o.selected = o.id === valueId));
      this.searchString = '';
      this.filterListSearch?.reset();
      this.setSearch('');
      this.dropDownEle.close();
    } else if (!selected) {
      this.currentSelectedValues = this.currentSelectedValues.filter(id => id !== valueId);
    } else {
      if (!this.currentSelectedValues.includes(valueId)) {
        this.currentSelectedValues.push(valueId);
      }
    }
    this.updatePreview();
    this.selectedValues.emit(this.currentSelectedValues);
    this.changedBeforeClose = true;
  }

  isOptionHidden(option: OptionsListMulti): boolean {
    return (!option.id && !this.showEmptyIds) || !!option.hide;
  }

  onMenuKeydown(event: KeyboardEvent): void {
    if (!this.singleSelection) {
      return;
    }
    const count = this.visibleOptions.length;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        const step = event.key === 'ArrowDown' ? 1 : -1;
        let index = this.activeIndex;
        for (let i = 0; i < count; i++) {
          index = (index + step + count) % count;
          if (!this.isOptionHidden(this.visibleOptions[index])) {
            this.activeIndex = index;
            this.scrollActiveIntoView();
            break;
          }
        }
        event.preventDefault();
        break;
      }
      case 'Enter': {
        const option = this.visibleOptions[this.activeIndex];
        if (option && !this.isOptionHidden(option)) {
          event.preventDefault();
          option.selected = true;
          this.setSelectValue(option.id, true);
        }
        break;
      }
    }
  }

  private scrollActiveIntoView(): void {
    setTimeout(() => this.elementRef.nativeElement.querySelector('.multi-select-option-active')?.scrollIntoView({ block: 'nearest' }));
  }

  openChange(event: boolean) {
    this.dropdownIsOpen = event;
    if (event) {
      this.activeIndex = this.visibleOptions.findIndex(o => o.selected && !this.isOptionHidden(o));
      if (this.singleSelection) {
        // Move focus into the menu so the arrow keys / enter reach onMenuKeydown
        setTimeout(() => {
          const menu: HTMLElement = this.elementRef.nativeElement.querySelector('.input-multi-select-list');
          const searchInput: HTMLElement = menu?.querySelector('input');
          if (searchInput && searchInput.offsetParent !== null) {
            searchInput.focus();
          } else {
            menu?.focus();
          }
        });
      }
    }
    if (!event && this.changedBeforeClose) {
      this.selectedValuesClose.emit(this.currentSelectedValues);
      this.changedBeforeClose = false;
    }
  }

  getPreviewField(option): string {
    if (this.previewField) {
      if (Array.isArray(this.previewField)) {
        const opts = this.previewField.map(field => {
          if (['', ' ', ',', ', ', ' - ', '-'].includes(field)) {
            return field;
          }
          return gungGetPropertyOfObject(field, option);
        });

        if (this.showEmptyIds) {
          const newOpts: string[] = [];
          for (let index = 0; index < opts.length; index++) {
            const previousOpt = opts[index - 1];
            const opt = opts[index];
            if (!(['', ' ', ',', ', ', ' - ', '-'].includes(opt) && !previousOpt)) {
              newOpts.push(opt);
            }
          }
          return newOpts.join('');
        }

        return opts.join('');
      }
      return option[this.previewField];
    }
    return option.id;
  }

}
